// frontend/src/hooks/useEnterNavigation.ts

import { useCallback, useRef, KeyboardEvent } from 'react';

interface UseEnterNavigationResult {
  // Callback ref to attach to a field. Tracks the focusable element by name.
  registerRef: (name: string) => (el: HTMLElement | null) => void;
  // Stable RefObject for the same field. Use for utilities that read ref.current
  // (e.g. scrollToFirstError).
  getObjectRef: (name: string) => React.RefObject<HTMLElement>;
  // onKeyDown handler. Enter advances to next field; Shift+Enter retreats.
  // The 'submit' field name is treated as the chain terminus: Enter is allowed
  // through so the browser fires the button's native click.
  getKeyHandler: (name: string) => (e: KeyboardEvent) => void;
  focusField: (name: string) => void;
  focusFirst: () => void;
}

export function useEnterNavigation(
  fieldNames: string[]
): UseEnterNavigationResult {
  const elementsRef = useRef<Record<string, HTMLElement | null>>({});
  const refCallbacksRef = useRef<
    Record<string, (el: HTMLElement | null) => void>
  >({});
  const objectRefsRef = useRef<Record<string, { current: HTMLElement | null }>>(
    {}
  );
  const orderRef = useRef<string[]>(fieldNames);
  orderRef.current = fieldNames;

  const ensureField = useCallback((name: string) => {
    if (!objectRefsRef.current[name]) {
      objectRefsRef.current[name] = { current: null };
    }
    if (!refCallbacksRef.current[name]) {
      refCallbacksRef.current[name] = (el: HTMLElement | null) => {
        elementsRef.current[name] = el;
        objectRefsRef.current[name].current = el;
      };
    }
  }, []);

  const registerRef = useCallback(
    (name: string) => {
      ensureField(name);
      return refCallbacksRef.current[name];
    },
    [ensureField]
  );

  const getObjectRef = useCallback(
    (name: string): React.RefObject<HTMLElement> => {
      ensureField(name);
      return objectRefsRef.current[name] as React.RefObject<HTMLElement>;
    },
    [ensureField]
  );

  const focusField = useCallback((name: string) => {
    elementsRef.current[name]?.focus();
  }, []);

  const focusFirst = useCallback(() => {
    const first = orderRef.current[0];
    if (first) focusField(first);
  }, [focusField]);

  // Treats native disabled (input/button/textarea) and aria-disabled (custom
  // controls like CustomSelect's combobox button or RadioButtonGroup labels)
  // as "skip me".
  const isDisabled = (el: HTMLElement | null): boolean => {
    if (!el) return true;
    if ((el as HTMLInputElement).disabled === true) return true;
    if (el.getAttribute('aria-disabled') === 'true') return true;
    return false;
  };

  // Walks the field order in `dir` from `fromIdx` and returns the next index
  // whose element is attached and not disabled. Returns -1 when none found.
  const nextEnabledIdx = useCallback((fromIdx: number, dir: 1 | -1): number => {
    const order = orderRef.current;
    let i = fromIdx + dir;
    while (i >= 0 && i < order.length) {
      const el = elementsRef.current[order[i]];
      if (!isDisabled(el)) return i;
      i += dir;
    }
    return -1;
  }, []);

  const getKeyHandler = useCallback(
    (name: string) => {
      return (e: KeyboardEvent) => {
        if (e.key !== 'Enter') return;
        // Skip during IME composition (e.g. CJK input)
        if ((e.nativeEvent as any).isComposing) return;

        const order = orderRef.current;
        const idx = order.indexOf(name);
        if (idx === -1) return;

        // Shift+Enter: go back one field (skipping disabled stops)
        if (e.shiftKey) {
          e.preventDefault();
          const prevIdx = nextEnabledIdx(idx, -1);
          if (prevIdx >= 0) focusField(order[prevIdx]);
          return;
        }

        // Submit terminus: let the browser fire the button's native click
        if (name === 'submit') return;

        // Advance to next enabled field
        e.preventDefault();
        const nIdx = nextEnabledIdx(idx, 1);
        if (nIdx >= 0) focusField(order[nIdx]);
      };
    },
    [focusField, nextEnabledIdx]
  );

  return { registerRef, getObjectRef, getKeyHandler, focusField, focusFirst };
}
